import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, CheckCircle, XCircle, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PERMISSION_LABELS = {
  crm: "CRM & Lead Management",
  users: "View Users",
  memberships: "View Memberships",
  businesses: "Manage Businesses",
  affiliates: "Manage Affiliates",
  applications: "Manage Applications",
  marketing: "AI Marketing & Email",
  analytics: "View Analytics",
  territories: "Manage Territories",
  sales: "View Sales"
};

export default function TeamInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid invite link');
      setLoading(false);
      return;
    }
    
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/api/auth/verify-team-invite/${token}`);
      if (response.data.valid) {
        setInviteData(response.data);
      } else {
        setError(response.data.message || 'Invalid or expired invite');
      }
    } catch (err) {
      setError('Failed to verify invite. The link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/api/auth/complete-team-invite`, {
        token,
        password
      });
      
      if (response.data.success) {
        setSuccess(true);
        // Auto-login the user
        login(response.data.token, response.data.user);
        
        // Redirect to admin after 2 seconds
        setTimeout(() => {
          navigate('/admin');
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto"></div>
          <p className="text-slate-400 mt-4">Verifying invite...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-900 border-slate-800">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Account Created!</h2>
            <p className="text-slate-400 mb-4">
              Welcome to Advanta Plus, {inviteData?.name}!
            </p>
            <p className="text-slate-500 text-sm">
              Redirecting to admin panel...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !inviteData) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-900 border-slate-800">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Invalid Invite</h2>
            <p className="text-slate-400 mb-6">{error}</p>
            <Button 
              onClick={() => navigate('/login')}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-900 border-slate-800">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 bg-gradient-to-br from-amber-400 to-amber-600 p-3 rounded-xl w-fit">
            <span className="text-slate-900 font-bold text-2xl">A+</span>
          </div>
          <CardTitle className="text-2xl text-white">Set Up Your Account</CardTitle>
          <CardDescription className="text-slate-400">
            Welcome to Advanta Plus, {inviteData?.name}!
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <Shield className="h-5 w-5 text-amber-500" />
              <span className="text-white font-medium">{inviteData?.role}</span>
            </div>
            <p className="text-sm text-slate-400 mb-2">Your access includes:</p>
            <div className="flex flex-wrap gap-2">
              {inviteData?.permissions?.map(perm => (
                <span 
                  key={perm}
                  className="px-2 py-1 bg-slate-700 rounded text-xs text-slate-300"
                >
                  {PERMISSION_LABELS[perm] || perm}
                </span>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input
                id="email"
                type="email"
                value={inviteData?.email || ''}
                disabled
                className="bg-slate-800 border-slate-700 text-slate-400"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Create Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="bg-slate-800 border-slate-700 text-white pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-300">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                className="bg-slate-800 border-slate-700 text-white"
                required
              />
            </div>

            {error && (
              <p className="text-red-400 text-sm">{error}</p>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900 font-semibold"
              data-testid="create-account-btn"
            >
              {submitting ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
